import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search, BookOpen, Library, ChevronLeft, ChevronRight, X } from "lucide-react";

export default function LibraryPage() {
  const [books, setBooks] = useState([]);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/lms/library", { params: { q, subject } }); setBooks(data.books || []); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openBook = async (b) => {
    const { data } = await api.get(`/lms/library/${b.id}`);
    setOpen(data.book); setPage(0);
  };

  return (
    <div className="space-y-6" data-testid="library-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ digital library</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2"><Library size={26} /> Library</h1>
        <p className="text-sm text-neutral-600 mt-1">Search and read books inside the platform. Downloads are restricted.</p>
      </div>

      <div className="bg-white border border-neutral-200 p-4 grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input className="w-full bg-white border border-neutral-300 pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] outline-none" placeholder="Search books..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="library-search" />
        </div>
        <select className="w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] outline-none" value={subject} onChange={(e) => { setSubject(e.target.value); }}>
          <option value="">All subjects</option>
          <option>Mathematics</option>
          <option>Science</option>
          <option>English</option>
          <option>History</option>
        </select>
        <button onClick={load} className="sm:col-span-3 bg-[#0A0C10] text-white font-bold py-2.5">Apply filters</button>
      </div>

      {loading ? (
        <div className="text-neutral-500">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
          {books.map((b) => (
            <button key={b.id} onClick={() => openBook(b)} className="bg-white p-5 text-left hover:bg-neutral-50">
              {b.cover_url && <img src={b.cover_url} alt={b.title} className="w-full h-40 object-cover" />}
              <div className="mt-3 font-display font-semibold">{b.title}</div>
              <div className="text-xs text-neutral-500 mt-1">{b.author} · {b.subject} · {b.grade}</div>
              <div className="text-xs text-neutral-600 mt-2 line-clamp-2">{b.description}</div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl max-h-[92vh] flex flex-col">
            <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ secure viewer · download disabled</div>
                <h2 className="font-display font-bold text-2xl">{open.title}</h2>
                <div className="text-xs text-neutral-500">{open.author}</div>
              </div>
              <button onClick={() => setOpen(null)}><X /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-8 bg-neutral-50 select-none" onContextMenu={(e) => e.preventDefault()}>
              <div className="bg-white border border-neutral-200 p-8 mx-auto max-w-2xl whitespace-pre-wrap font-mono text-sm leading-7">
                {open.pages?.[page] || "Empty page."}
              </div>
            </div>
            <div className="p-3 border-t border-neutral-200 flex items-center justify-between">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="text-sm px-3 py-1.5 border border-neutral-300 disabled:opacity-50 inline-flex items-center gap-1"><ChevronLeft size={14} /> Prev</button>
              <div className="text-xs text-neutral-500">Page {page + 1} of {open.pages?.length || 1}</div>
              <button onClick={() => setPage((p) => Math.min((open.pages?.length || 1) - 1, p + 1))} disabled={page >= (open.pages?.length || 1) - 1} className="text-sm px-3 py-1.5 border border-neutral-300 disabled:opacity-50 inline-flex items-center gap-1">Next <ChevronRight size={14} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
